import { useCallback, useEffect, useState } from "react";
import { MotionConfig } from "framer-motion";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { Toaster } from "sonner";
import "./fonts";
import "./noorin.css";
import { useLang } from "./i18n/LangContext";
import { coreState, hasWebGL } from "./lib/util";
import { Nav } from "./components/Nav";
import { Preloader } from "./components/Preloader";
import { Backdrop } from "./components/Backdrop";
import { CoreStage } from "./components/CoreStage";
import { MobileCTA } from "./components/MobileCTA";
import { Hero } from "./sections/Hero";
import { Marquee } from "./sections/Marquee";
import { Clarity } from "./sections/Clarity";
import { Journey } from "./sections/Journey";
import { Capabilities } from "./sections/Capabilities";
import { Packages } from "./sections/Packages";
import { Human } from "./sections/Human";
import { Final } from "./sections/Final";
import { Footer } from "./sections/Footer";

const useLenis = () => {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ autoRaf: true, lerp: 0.085 });
    window.__lenis = lenis;
    return () => {
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
};

const useIntro = () => {
  const [coreReady, setCoreReady] = useState(!hasWebGL());
  const [minPassed, setMinPassed] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => {
    const a = setTimeout(() => setMinPassed(true), 1600);
    const b = setTimeout(() => setDone(true), 4000);
    return () => {
      clearTimeout(a);
      clearTimeout(b);
    };
  }, []);
  useEffect(() => {
    if (minPassed && coreReady) setDone(true);
  }, [minPassed, coreReady]);
  useEffect(() => {
    coreState.lit = done;
  }, [done]);
  return { done, onReady: useCallback(() => setCoreReady(true), []) };
};

export default function NoorinHome() {
  const { isFa } = useLang();
  const { done, onReady } = useIntro();
  useLenis();
  return (
    <MotionConfig reducedMotion="user">
      <div className={`noorin ${isFa ? "is-fa" : "is-en"}`} data-testid="noorin-home">
        <Preloader done={done} />
        <Backdrop />
        <CoreStage onReady={onReady} />
        <Nav />
        <main className="relative z-[2]">
          <Hero ready={done} />
          <Marquee />
          <Clarity />
          <Journey />
          <Capabilities />
          <Packages />
          <Human />
          <Final />
        </main>
        <Footer />
        <MobileCTA />
        <div aria-hidden className="grain" />
        <Toaster position="bottom-center" toastOptions={{ className: "noorin-toast" }} />
      </div>
    </MotionConfig>
  );
}
